/**
 * UwBedingungenDialog - open an E6 condition round: the carrier's wording + the structured delta,
 * delivered to the customer via portal or e-mail with a Frist (P2 Slice 14 D3).
 */

import { useEffect, useState } from 'react';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';
import { ANTRAG_KEIN_PORTAL_HINWEIS, UW_RUNDE_OFFEN_HINWEIS, type UnderwritingResponse, type UwBedingungenBody } from '@brokly/shared';

const inputCls = 'w-full rounded-md border bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const ERROR_TEXT: Record<string, string> = {
  versand_unterbrochen: 'Versand unterbrochen - bitte zuerst aufloesen',
  runde_offen: UW_RUNDE_OFFEN_HINWEIS,
  frist_invalid: 'Die Frist liegt in der Vergangenheit',
  kunde_email_fehlt: 'Beim Kunden ist keine E-Mail-Adresse hinterlegt',
  communication_send_required: 'Fuer den E-Mail-Versand fehlt die Berechtigung',
  mailbox_not_connected: 'Kein Postfach verbunden',
  mailbox_reauth_required: 'Das Postfach muss neu verbunden werden',
  mail_send_blocked: 'Der Versand an diese Adresse ist blockiert',
  mailbox_send_failed: 'Der E-Mail-Versand ist fehlgeschlagen',
  antrag_status_invalid: 'Der Antrag ist nicht mehr in diesem Status',
};

function parseNum(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function UwBedingungenDialog({
  open,
  onClose,
  facts,
  onSubmit,
  saving,
  error,
}: {
  open: boolean;
  onClose: () => void;
  facts: UnderwritingResponse;
  onSubmit: (body: UwBedingungenBody) => void;
  saving: boolean;
  error: string | null;
}) {
  const a = facts.antrag;
  const [text, setText] = useState('');
  const [praemie, setPraemie] = useState('');
  const [summe, setSumme] = useState('');
  const [selbst, setSelbst] = useState('');
  const [beginn, setBeginn] = useState('');
  const [dauer, setDauer] = useState('');
  const [ausschluesse, setAusschluesse] = useState('');
  const [channel, setChannel] = useState<'portal' | 'email'>(facts.kanal_vorschlag);
  const [frist, setFrist] = useState(facts.frist_vorschlag);

  useEffect(() => {
    if (open) {
      setText('');
      setPraemie('');
      setSumme('');
      setSelbst('');
      setBeginn('');
      setDauer('');
      setAusschluesse('');
      setChannel(facts.kanal_vorschlag);
      setFrist(facts.frist_vorschlag);
    }
  }, [open, facts.kanal_vorschlag, facts.frist_vorschlag]);

  const submit = () => {
    const liste = ausschluesse.split('\n').map((s) => s.trim()).filter((s) => s.length > 0);
    onSubmit({
      bedingungen_text: text.trim(),
      praemie_jahr_neu: parseNum(praemie),
      versicherungssumme_neu: parseNum(summe),
      selbstbehalt_neu: parseNum(selbst),
      begin_date_neu: beginn.trim() || null,
      vertragsdauer_monate_neu: parseNum(dauer),
      ausschluesse_neu: liste,
      channel,
      frist_date: frist,
    });
  };

  const altStr = (v: number | null): string => (v === null ? '-' : String(v));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Geaenderte Bedingungen erfassen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Zurueck</Button>
          <Button onClick={submit} disabled={saving || text.trim().length < 3} data-testid="uw-bedingungen-submit">
            An Kunden zur Freigabe senden
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="uw-bedingungen-dialog">
        <label className="text-sm">
          Geaenderte Bedingungen (Wortlaut)
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className={inputCls} data-testid="uw-bedingungen-text" />
        </label>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <label>Jahrespraemie (alt {altStr(a?.praemie_jahr ?? null)})<input value={praemie} onChange={(e) => setPraemie(e.target.value)} className={inputCls} data-testid="uw-bedingungen-praemie" /></label>
          <label>Versicherungssumme (alt {altStr(a?.versicherungssumme ?? null)})<input value={summe} onChange={(e) => setSumme(e.target.value)} className={inputCls} /></label>
          <label>Selbstbehalt (alt {altStr(a?.selbstbehalt ?? null)})<input value={selbst} onChange={(e) => setSelbst(e.target.value)} className={inputCls} data-testid="uw-bedingungen-selbstbehalt" /></label>
          <label>Versicherungsbeginn (alt {a?.begin_date ?? '-'})<input type="date" value={beginn} onChange={(e) => setBeginn(e.target.value)} className={inputCls} /></label>
          <label>Vertragsdauer (Monate) (alt {altStr(a?.vertragsdauer_monate ?? null)})<input value={dauer} onChange={(e) => setDauer(e.target.value)} className={inputCls} /></label>
        </div>
        <label className="text-sm">
          Neue Ausschluesse (eine pro Zeile)
          <textarea value={ausschluesse} onChange={(e) => setAusschluesse(e.target.value)} rows={2} className={inputCls} />
        </label>
        <fieldset className="text-sm">
          <legend className="text-muted-foreground">Kanal</legend>
          <label className="mr-4"><input type="radio" name="uw-bed-kanal" checked={channel === 'portal'} disabled={!facts.portal_verfuegbar} onChange={() => setChannel('portal')} /> Kundenportal</label>
          <label><input type="radio" name="uw-bed-kanal" checked={channel === 'email'} disabled={!facts.kunde_email} onChange={() => setChannel('email')} /> E-Mail an {facts.kunde_email ?? '-'}</label>
          {!facts.portal_verfuegbar && <p className="text-xs text-muted-foreground">{ANTRAG_KEIN_PORTAL_HINWEIS}</p>}
        </fieldset>
        <label className="text-sm">
          Frist fuer die Freigabe
          <input type="date" value={frist} onChange={(e) => setFrist(e.target.value)} className={inputCls} data-testid="uw-bedingungen-frist" />
        </label>
        {error && <p role="alert" className="text-sm text-status-danger" data-testid="uw-bedingungen-error">{ERROR_TEXT[error] ?? error}</p>}
      </div>
    </Dialog>
  );
}
